import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import type { LanguageKey } from '@softgames/localization';
import { v1 as uuidv1 } from 'uuid';
import { GAME_ECONOMY_SETTINGS } from 'game/GameConstants';
import type { LevelData } from '../../game/level/LevelData';
import { getLevelData, getLevelHash, getTotalLevels } from '../../../levels';
import { INITIAL_STATE } from './AppState';
import type { AppState } from './AppState';
import type { LegacyAppState } from './legacy/LegacyAppState';
import { convertLegacyAppState } from './legacy/LegacyAppState';
import type { Platform } from '../platform/Platform';
import type { LivesState } from './lives/LivesState';
import * as LivesStateUtils from './lives/LivesStateUtils';
import * as DioramaStateUtils from './diorama/DioramaStateUtils';
import { AppStateSubscriptions } from './AppStateSubscriptions';
import { extractAppStateChanges } from './extractAppStateChanges';
import type { BoosterType } from '../../game/boosters/types';
import type { LevelTaskTallies } from '../../game/tasks/types';
import { convertLevelScoreToTaskTallies, hasTaskTally } from '../../game/tasks/LevelTasksUtils';
import type { FeatureCooldownType } from './cooldowns/types';
import type { EntryPointPayloadRecord } from '../entryPoints/types';
import { getTimeUntilFeatureAvailable } from './cooldowns/getTimeUntilFeatureAvailable';
import { isEntryPointPayloadExpired } from '../entryPoints/isEntryPointPayloadExpired';
import type { DioramaState } from './diorama/dioramaState';
import type { TournamentSaveData } from '../tournaments/types';
import * as PiggyBankUtils from '../../gameEvents/piggyBank/PiggyBankUtils';
import type { PiggyBankState } from '../../gameEvents/piggyBank/PiggyBankState';
import { BlockerType } from '../../game/boardMVC/types';
import { daysSinceEpoch } from '../../utils/time-utils';
import { dailyRewardConfig } from '../../behaviours/openDailyRewardPopup';
import { migrateOfferData } from '../offers/migrateOfferData';
import type { OfferSystemData } from '../../libs/offerSystem/OfferSystemHistory';
import type { OldPurchaseHistoryEntry } from '../iap/types';
import { LIVES_MAX } from './lives/LivesConstants';
import { getNextSpinTime } from '../../gameEvents/luckyWheel/LuckyWheelUtils';

export interface LevelTrackingData {
  levelNumber: number;
  levelId: string;
  levelAttemptId: string;
  lives: number;
  tileRvAmount: number;
}

export interface ProgressSaveData {
  levelData: Pick<LevelData, 'board' | 'tray' | 'tutorial'>;
  levelTaskTallies: LevelTaskTallies;
  levelReviveUsed: number;
}

export class AppStateController {
  public readonly name = 'AppState';

  public readonly subscriptions: AppStateSubscriptions = new AppStateSubscriptions();

  private data!: AppState;

  private platform: Platform | null = null;

  private isDev = false;

  public async init(platform: Platform, isDev: boolean): Promise<void> {
    this.platform = platform;
    this.isDev = isDev;

    let savedState = (await this.loadState()) as Partial<AppState> | null;
    if (savedState) {
      // Returning User
      // Migrate data from original prototype.
      const isLegacyState = savedState.levelIndex === 0 && !!savedState.state;
      if (isLegacyState) savedState = convertLegacyAppState(savedState.state as unknown as LegacyAppState);

      // Merge the saved state with the default state.
      const state: AppState = { ...INITIAL_STATE, ...savedState };
      state.sessionCount += 1;
      state.appVer = APP_VERSION;
      state.totalLevels = getTotalLevels();

      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const oldPurchaseHistory = (savedState?.iap as { purchaseHistory?: OldPurchaseHistoryEntry[] })
          ?.purchaseHistory;
        if (oldPurchaseHistory) {
          state.iap = { offerSystemData: migrateOfferData(oldPurchaseHistory) };
        }
      } catch (e) {
        console.warn(e);
      }

      // Merge boosters with defaults
      state.boosters = { ...INITIAL_STATE.boosters, ...state.boosters };

      // Merge iap with defaults
      state.iap = { ...INITIAL_STATE.iap, ...state.iap };

      state.piggyBank = { ...PiggyBankUtils.getDefaultState(), ...state.piggyBank };

      state.luckyWheel = { ...INITIAL_STATE.luckyWheel, ...state.luckyWheel };

      // Converts legacy 'levelScore'.
      if (state.levelScore > 0) {
        state.levelTaskTallies = convertLevelScoreToTaskTallies(state.levelScore);
        state.levelScore = 0;
      }

      // Refill lives based on previous timestamp.
      LivesStateUtils.refillLives(state.lives);
      DioramaStateUtils.tryTransitionToNextDiorama(state.diorama);

      // remove expired payloads.
      state.entryPayloads = state.entryPayloads.filter((record) => !isEntryPointPayloadExpired(record));

      void this.setData(state, true);
    } else {
      // New User
      void this.setData(INITIAL_STATE, true);
    }

    this.applyEconomyRebalance();
    this.checkDailyRewardForNewDay();
    this.checkFreeShopGiftForNewDay();
  }

  // if levels are modified by split test, having that in init
  // would result in resetting progress
  public postSplitTestInit(): void {
    const { data } = this;
    // Reset level progress if the level hash changed.
    const levelHash = getLevelHash(data.levelIndex);
    if (getLevelHash(data.levelIndex) !== data.levelHash) {
      const levelData = getLevelData(data.levelIndex);
      data.levelAttemptId = uuidv1();
      data.levelData = cloneDeep({ board: levelData.board, tray: levelData.tray });
      data.levelTaskTallies = null;
      data.levelHash = levelHash;
    }
  }

  private async loadState(): Promise<Partial<AppState>> {
    if (!this.platform) {
      throw new Error('AppStateController: Platform is not set');
    }
    return this.platform.loadStateAsync();
  }

  public async resetState(): Promise<void> {
    return this.setData(INITIAL_STATE, true, true);
  }

  public exportState(): AppState {
    return { ...this.data };
  }

  public async setData(change: Partial<AppState>, writeFullState = false, override = false): Promise<void> {
    const prevState: AppState = { ...this.data };

    const changes: Partial<AppState> = extractAppStateChanges(change, prevState);
    if (!Object.keys(changes).length) return;

    this.data = { ...this.data, ...changes } as AppState;

    this.subscriptions.analyzeChanges(changes, prevState);

    if (!this.platform) {
      throw new Error('AppStateController: Platform is not set');
    }

    if (override) {
      this.data = { ...changes } as AppState;
    }

    await this.platform.saveStateAsync(this.data, changes, writeFullState);
  }

  public onTick(): void {
    this.checkLivesRefill();
  }

  public getLanguage(): LanguageKey | null {
    return this.data.language;
  }

  public setLanguage(language: LanguageKey): void {
    void this.setData({ language });
  }

  public get currentLevelData(): LevelData {
    const { originalLevelData } = this;
    if (!this.data.levelData) return originalLevelData;

    return cloneDeep({
      ...originalLevelData,
      ...this.data.levelData,
    });
  }

  public get originalLevelData(): LevelData {
    return getLevelData(this.data.levelIndex);
  }

  public updateCurrentLevelProgress(progress: ProgressSaveData): void {
    void this.setData(cloneDeep(progress));
  }

  public hasCurrentLevelProgress(): boolean {
    const { levelIndex, levelTaskTallies, levelData } = this.data;
    if (levelTaskTallies && hasTaskTally(levelTaskTallies)) return true;
    if (!levelData?.board || !levelData?.tray) return false;

    const originalLevelData = getLevelData(levelIndex);

    return !isEqual(originalLevelData.tray, levelData.tray) || !isEqual(originalLevelData.board, levelData.board);
  }

  private setLevelDataByIndex(levelIndex: number): LevelData {
    const { data } = this;
    const isNewLevel = data.levelIndex !== levelIndex;
    const levelData = getLevelData(levelIndex);
    const levelHash = getLevelHash(levelIndex);
    const levelAttemptId = uuidv1();

    void this.setData({
      levelIndex,
      levelTaskTallies: {},
      levelReviveUsed: 0,
      levelStarted: false,
      levelAttempts: isNewLevel ? 0 : data.levelAttempts + 1,
      levelAttemptId,
      levelData: { board: levelData.board, tray: levelData.tray },
      levelHash,
    });
    return cloneDeep(levelData);
  }

  public setNextLevel(): LevelData {
    const { levelIndex } = this.data;
    const nextLevelIndex = levelIndex + 1;
    return this.setLevelDataByIndex(nextLevelIndex);
  }

  public setPreviousLevel(): LevelData {
    const { levelIndex } = this.data;
    const prevLevelIndex = Math.max(levelIndex - 1, 0);
    return this.setLevelDataByIndex(prevLevelIndex);
  }

  public resetLevel(): LevelData {
    const { levelIndex } = this.data;
    return this.setLevelDataByIndex(levelIndex);
  }

  public getLevelIndex(): number {
    return this.data.levelIndex;
  }

  public getLevelNumber(): number {
    return this.data.levelIndex + 1;
  }

  public getLevelTaskTallies(): LevelTaskTallies {
    return cloneDeep(this.data.levelTaskTallies ?? {});
  }

  public getLevelReviveUsedCount(): number {
    return this.data.levelReviveUsed;
  }

  public getLevelAttempts(): number {
    return this.data.levelAttempts;
  }

  public getLevelTrackingData(): LevelTrackingData {
    const tileRvAmount =
      this.currentLevelData?.board?.blockers?.reduce((acc, cur) => acc + (cur.type === BlockerType.Ad ? 1 : 0), 0) ?? 0;

    return {
      levelAttemptId: this.data.levelAttemptId ?? '',
      levelId: this.data.levelHash ?? '',
      levelNumber: this.getLevelNumber(),
      lives: this.getLives(),
      tileRvAmount,
    };
  }

  public flagLevelStarted(): void {
    void this.setData({ levelStarted: true });
  }

  public isLevelStarted(): boolean {
    return this.data.levelStarted;
  }

  public getCoins(): number {
    return this.data.coins;
  }

  public canAffordCoinCost(coins: number): boolean {
    return this.getCoins() >= coins;
  }

  public setCoins(coins: number): void {
    void this.setData({ coins: Math.max(0, coins) });
  }

  public getLives(): number {
    return this.data.lives.count;
  }

  public getLivesMax(): number {
    return this.data.lives.maxLives ?? LIVES_MAX;
  }

  public setLivesMax(newMax: number): void {
    void this.setData({
      lives: {
        ...this.data.lives,
        maxLives: newMax,
      },
    });
  }

  public setLives(lives: number): void {
    const livesState: LivesState = { ...this.data.lives };

    const deltaLives = lives - livesState.count;
    if (deltaLives < 0) LivesStateUtils.removeLives(livesState, Math.abs(deltaLives));
    else livesState.count = lives;

    void this.setData({ lives: livesState });
  }

  public getTimeUntilNextLife(): number {
    return LivesStateUtils.getTimeUntilNextLife(this.data.lives);
  }

  private checkLivesRefill(): void {
    if (LivesStateUtils.canRefillLife(this.data.lives)) {
      const livesState: LivesState = { ...this.data.lives };
      LivesStateUtils.refillLives(livesState);
      void this.setData({ lives: livesState });
    }
  }

  public setUnlimitedLivesMinutes(minutes: number): void {
    const livesState: LivesState = { ...this.data.lives };
    LivesStateUtils.setUnlimitedLivesTime(livesState, minutes);
    if (minutes > 0) LivesStateUtils.setMaxLives(livesState);
    void this.setData({ lives: livesState });
  }

  public getUnlimitedLivesMinutes(): number {
    return LivesStateUtils.getUnlimitedLivesMinutes(this.data.lives);
  }

  public hasLivesToPlay(): boolean {
    return this.getUnlimitedLivesMinutes() > 0 || this.getLives() > 0;
  }

  public getBoosterInventoryAmount(boosterType: BoosterType): number {
    return this.data.boosters?.[boosterType] ?? 0;
  }

  public setBoosterInventoryAmount(boosterType: BoosterType, amount: number): void {
    void this.setData({ boosters: { ...this.data.boosters, [boosterType]: Math.max(0, amount) } });
  }

  public getFreeSpins(): number {
    return this.data.luckyWheel.rewardedFreeSpins;
  }

  public setFreeSpins(amount: number): void {
    const freeSpins = this.getFreeSpins();

    const allowBonusFreeSpin = freeSpins === 0 && Date.now() > getNextSpinTime(this.data.luckyWheel.lastFreeSpinTime);

    void this.setData({
      luckyWheel: {
        ...this.data.luckyWheel,
        rewardedFreeSpins: Math.max(0, amount),
        bonusFreeSpinTime: allowBonusFreeSpin ? Date.now() : 0,
      },
    });
  }

  public setSfxVolume(volume: number): void {
    void this.setData({ sfxVolume: volume });
  }

  public getSfxVolume(): number {
    return this.data.sfxVolume;
  }

  public setMusicVolume(volume: number): void {
    void this.setData({ musicVolume: volume });
  }

  public getMusicVolume(): number {
    return this.data.musicVolume;
  }

  public isFirstSession(): boolean {
    return this.data.sessionCount === 1;
  }

  public getSessionCount(): number {
    return this.data.sessionCount;
  }

  public getActiveSplitTests(): Record<string, number> {
    return { ...this.data.splitTest };
  }

  public setActiveSplitTests(splitTests: Record<string, number>): void {
    void this.setData({ splitTest: { ...splitTests } });
  }

  public setNewLeaderboardPool(pool: number, startLevel: number): void {
    void this.setData({
      leaderboard: {
        pool,
        startLevel,
        joinedAt: Date.now(),
      },
    });
  }

  public getLeaderboardPool(): AppState['leaderboard'] {
    return { ...this.data.leaderboard };
  }

  public addEntryPayload(record: EntryPointPayloadRecord): void {
    const entryPayloads = [...this.data.entryPayloads, record];
    void this.setData({ entryPayloads });
  }

  public hasEntryPayload(uid: string): boolean {
    return this.data.entryPayloads.some((payload) => payload.uid === uid);
  }

  public getFeatureCooldownTime(feature: FeatureCooldownType): number {
    return getTimeUntilFeatureAvailable(feature, this.data.featureCooldowns);
  }

  public setFeatureCooldownTime(feature: FeatureCooldownType, timestamp: number): void {
    void this.setData({
      featureCooldowns: { ...this.data.featureCooldowns, [feature]: timestamp },
    });
  }

  public getCurrentDioramaState(): DioramaState {
    return { ...this.data.diorama };
  }

  public canAddProgressToDiorama(): boolean {
    return !DioramaStateUtils.isDioramaAtEndOfContent(this.data.diorama);
  }

  public increaseDioramaProgress(tilesAmount: number): DioramaState {
    void this.setData({
      diorama: DioramaStateUtils.increaseDioramaProgress(this.getCurrentDioramaState(), tilesAmount),
    });
    return this.getCurrentDioramaState();
  }

  public getPiggyBankState(): PiggyBankState {
    return cloneDeep(this.data.piggyBank ?? PiggyBankUtils.getDefaultState());
  }

  public addCoinsToPiggyBank(coins: number): void {
    void this.setData({ piggyBank: cloneDeep(PiggyBankUtils.addCoins(this.getPiggyBankState(), coins)) });
  }

  public resetPiggyBankState(): void {
    void this.setData({ piggyBank: cloneDeep(PiggyBankUtils.getDefaultState()) });
  }

  public getSplitTestAdditionalData<T extends keyof AppState['splitTestData'] = keyof AppState['splitTestData']>(
    key: T,
  ): AppState['splitTestData'][T] {
    return this.data.splitTestData[key];
  }

  public setSplitTestAdditionalData<T extends keyof AppState['splitTestData'] = keyof AppState['splitTestData']>(
    key: T,
    value: AppState['splitTestData'][T],
  ): void {
    void this.setData({
      splitTestData: {
        ...this.data.splitTestData,
        [key]: value,
      },
    });
  }

  public getSavedTournaments(): TournamentSaveData[] {
    return cloneDeep(this.data.tournaments);
  }

  public setSavedTournaments(tournaments: TournamentSaveData[]): void {
    void this.setData({
      tournaments,
    });
  }

  public isHorizontalLayoutActive(): boolean {
    return this.data.horizontalLayout;
  }

  public setHorizontalLayout(value: boolean): void {
    void this.setData({
      horizontalLayout: value,
    });
  }

  public setAddedShortcut(date: number | null): void {
    void this.setData({
      shortcut: {
        addedAt: date,
      },
    });
  }

  public getAddedShortcutDate(): number | null {
    return this.data.shortcut.addedAt;
  }

  public debugSetData(change: Partial<AppState>): void {
    void this.setData(change);
  }

  private checkDailyRewardForNewDay(): void {
    if (this.data.dailyReward.day === daysSinceEpoch()) return;
    void this.setData({
      dailyReward: {
        day: daysSinceEpoch(),
        freeRewardOpened: false,
        adRewardsOpened: 0,
      },
    });
  }

  public getDailyRewardData(): AppState['dailyReward'] {
    return cloneDeep(this.data.dailyReward);
  }

  public markDailyFreeRewardAsOpened(): void {
    void this.setData({
      dailyReward: {
        ...this.data.dailyReward,
        freeRewardOpened: true,
      },
    });
  }

  public markDailyAdRewardAsOpened(): void {
    void this.setData({
      dailyReward: {
        ...this.data.dailyReward,
        adRewardsOpened: this.data.dailyReward.adRewardsOpened + 1,
      },
    });
  }

  public hasAnyDailyRewardsToOpen(): boolean {
    const {
      data: {
        dailyReward: { freeRewardOpened, adRewardsOpened },
      },
    } = this;
    return !freeRewardOpened || Boolean(dailyRewardConfig.adOffers[adRewardsOpened]);
  }

  public applyEconomyRebalance(): void {
    if (this.data.economyRebalanceDate != null) return;

    void this.setData({
      economyRebalanceDate: Date.now(),
    });

    // if the user is a first time user, no rebalance is required
    if (this.isFirstSession()) {
      return;
    }

    if (this.isDev) {
      console.log(`[Economy Rebalance] coins from ${this.data.coins} to ${this.data.coins * 8}`);
    }

    this.setCoins(this.data.coins * 8);

    if (this.data.piggyBank?.coins) {
      // calculate the old percentage of the piggy bank
      const progress = this.data.piggyBank.coins / this.data.piggyBank.maxCoins;

      const rebalancedPiggyBankCoins = Math.ceil(progress * GAME_ECONOMY_SETTINGS.piggyBankCoinCapacity * 0.1) * 10;

      if (this.isDev) {
        console.log(
          `[Economy Rebalance] piggy bank from ${this.data.piggyBank.coins}/${this.data.piggyBank.maxCoins} to ${rebalancedPiggyBankCoins}/${GAME_ECONOMY_SETTINGS.piggyBankCoinCapacity}`,
        );
      }

      this.resetPiggyBankState();
      this.addCoinsToPiggyBank(rebalancedPiggyBankCoins);
    }
  }

  public getLastWinStreakEndTime(): number {
    return this.data.winStreakEventLastEndTime;
  }

  public getLastRaceEventEndTime(): number {
    return this.data.raceEventLastEndTime;
  }

  public getUserFirstLoginDate(): number {
    return this.data.userFirstLogin;
  }

  public getOfferSystemData(): OfferSystemData {
    return cloneDeep(this.data.iap.offerSystemData ?? {});
  }

  public setOfferSystemData(value: OfferSystemData): void {
    void this.setData({
      iap: {
        offerSystemData: value,
      },
    });
  }

  public getData(): AppState {
    return this.data;
  }

  public shouldTrackOfferShow(offer: { id: string; activationId: string }): boolean {
    if (!this.data.trackingOfferShow[offer.id]) return true;
    return this.data.trackingOfferShow[offer.id] !== offer.activationId;
  }

  public markOfferShowTracking(offer: { id: string; activationId: string }): void {
    void this.setData({
      trackingOfferShow: {
        ...this.data.trackingOfferShow,
        [offer.id]: offer.activationId,
      },
    });
  }

  public checkFreeShopGiftForNewDay(): void {
    const now = Date.now();
    if (now > this.getNextFreeShopGiftDate()) {
      void this.setData({
        freeShopGift: {
          ...this.data.freeShopGift,
          giftOpened: false,
          giftShown: false,
        },
      });
    }
  }

  public getNextFreeShopGiftDate(): number {
    if (!this.data.freeShopGift.giftOpened) return 0;

    const nextDay = new Date(this.data.freeShopGift.claimedAt);
    nextDay.setDate(nextDay.getDate() + 1);
    nextDay.setHours(0, 0, 0, 0);

    return nextDay.getTime();
  }

  public getFreeShopGiftData(): AppState['freeShopGift'] {
    return cloneDeep(this.data.freeShopGift);
  }

  public markFreeShopGiftAsOpened(): void {
    void this.setData({
      freeShopGift: {
        ...this.data.freeShopGift,
        claimedAt: Date.now(),
        giftOpened: true,
      },
    });
  }

  public markFreeShopGiftAsShown(): void {
    if (!this.hasSeenFreeShopGiftToday()) {
      void this.setData({
        freeShopGift: {
          ...this.data.freeShopGift,
          giftShown: true,
        },
      });
    }
  }

  public resetFreeShopGiftData(): void {
    void this.setData({
      freeShopGift: {
        claimedAt: 0,
        giftOpened: false,
        giftShown: false,
      },
    });
  }

  public hasSeenFreeShopGiftToday(): boolean {
    return this.data.freeShopGift.giftShown;
  }

  public hasFreeShopGiftToOpen(): boolean {
    return !this.data.freeShopGift.giftOpened;
  }

  public setCheatMenuEnabled(value: boolean): void {
    void this.setData({
      cheatEnabled: value,
    });
  }

  public getCheatMenuEnabled(): boolean {
    return this.data.cheatEnabled;
  }

  public setPurchasedProducts(products: string[]): void {
    void this.setData({
      purchasedProducts: [...this.data.purchasedProducts, ...products],
    });
  }

  public getPurchasedProducts(): string[] {
    return [...this.data.purchasedProducts];
  }
}
